import { SubscriptionsService } from "./base";
import { DriveStore } from "../store/reducers";
import { Dialog, IDialogButton } from "../ui/framework/dialog";
import idSerializer from "geotab-uuid";
import { INativeNotifyAction, TNativeActionEventCallback,
         INativeActionEvent, INativeNotify, getNotificationController } from "../utils/native/notificationController";
import TranslationService from "./translation";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { Unsubscribe } from "../lib/redux";
import { observeStore } from "../utils/store";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import * as logger from "../utils/logger";
import Translator from "../utils/translator";
import { AppType, getAppType } from "../utils/native/nativeHelpers";
import { htmlEscape } from "../utils/vanilla";

export type TPromptActionType = "primary" | "secondary";

export interface IPromptAction {
    id: string;
    name: string;
    action: (dialog: Dialog, e: Event) => void;
    type?: TPromptActionType;
}

class AlertPrompt {
    id: string;
    nativeNotification?: INativeNotify;
    dialog: Dialog;
    actions?: { [actionName: string]: IPromptAction };
    translate: boolean;

    /**
     * Constructs a new instance of the AlertPrompt
     * @param id The ID of the alert
     * @param dialog The UI Dialog instance
     * @param actions THe list of actions to perform
     * @param translate Whether or not to translate the dialog
     */
    constructor(id: string, dialog: Dialog, actions?: IPromptAction[], translate = true) {
        this.id = id;
        this.dialog = dialog;
        actions && actions.forEach(action => {
            this.actions = this.actions || {};
            this.actions[action.id] = action;
        });
        this.translate = translate;
    }

    /**
     * Whether or not the alert was created while the app was in the background
     */
    get isBackground(): boolean {
        return !!this.nativeNotification;
    }

    /**
     * Gets the native OS notification's ID as a string if the alert was created while the app was in the background
     */
    get nativeId(): string | undefined {
        if (this.nativeNotification && !isNaN(this.nativeNotification.id)) {
            return this.nativeNotification.id.toString();
        }
        return undefined;
    }

    /**
     * Opens a UI dialog
     * @param translator The translator
     */
    openDialog(translator: Translator): Promise<Dialog> {
        return this.dialog && this.dialog.open(translator, this.translate ? undefined : "en");
    }

    /**
     * The full action id for a native OS notification
     * @param actionId The action ID
     */
    getFullActionId(actionId: string) {
        return `${ this.id }|${ actionId }`;
    }

    /**
     * Triggers an action
     * @param actionId The action ID to trigger
     */
    triggerAction(actionId: string) {
        if (!this.actions) {
            return;
        }
        let action = this.actions[actionId];
        if (action) {
            let domEvent: CustomEvent = new CustomEvent("nativeNotificationClick", {
                detail: {
                    id: action.id,
                    innerText: action.name,
                    tagName: "NATIVENOTIFICATION"
                }
            });
            dispatcher.trigger(DispatcherEvents.DriveAppInteraction, domEvent);
            action.action(this.dialog, new Event("click"));
        }
    }
}

/**
 * Un-registers listeners for native OS notification actions
 * @param alertPrompt The alert prompt
 * @param actionListenerCallback The event listener callback
 */
function unregisterActionListeners(alertPrompt: AlertPrompt | null, actionListenerCallback: TNativeActionEventCallback) {
    alertPrompt && alertPrompt.actions && Object.keys(alertPrompt.actions).forEach(actionId => {
        if (!alertPrompt.actions) {
            return;
        }
        let action = alertPrompt.actions[actionId];
        let fullActionId = alertPrompt.getFullActionId(action.id);
        getNotificationController().nativeNotifyEventOff(fullActionId, actionListenerCallback);
    });
}

/**
 * Transforms the alert prompt's actions into native OS notification actions
 * @param alertPrompt The alert prompt
 */
function getNativeNotificationActions(alertPrompt: AlertPrompt): INativeNotifyAction[] {
    return alertPrompt.actions ? Object.keys(alertPrompt.actions).reduce((nativeNotifyActions, actionId) => {
        if (!alertPrompt.actions) {
            return nativeNotifyActions;
        }
        let action = alertPrompt.actions[actionId];
        let nativeNotifyAction: INativeNotifyAction = {
            id: alertPrompt.getFullActionId(action.id),
            title: action.name,
            launch: true
        };
        if (action.type === "secondary") {
            nativeNotifyAction.ui = "decline";
        }
        nativeNotifyActions.push(nativeNotifyAction);
        return nativeNotifyActions;
    }, [] as INativeNotifyAction[]) : [];
}

/**
 * Registers listeners for native OS notification actions
 * @param alertPrompt The alert prompt
 * @param actionListenerCallback The event listener callback
 */
function registerActionListeners(alertPrompt: AlertPrompt | null, actionListenerCallback: TNativeActionEventCallback): string[] {
    return alertPrompt && alertPrompt.actions ? Object.keys(alertPrompt.actions).reduce((actionIds, actionId) => {
        if (!alertPrompt.actions) {
            return actionIds;
        }
        let action = alertPrompt.actions[actionId];
        let fullActionId = alertPrompt.getFullActionId(action.id);
        getNotificationController().nativeNotifyEventOn(fullActionId, actionListenerCallback);
        actionIds.push(fullActionId);
        return actionIds;
    }, [] as string[]) : [];
}

export default class Alerts extends SubscriptionsService {
    private readonly store: DriveStore;
    private readonly translationService: TranslationService;

    private pendingBackgroundAlerts: { [nativeNotificationId: string]: AlertPrompt } = {};

    /**
     * One stable reference for every native notification on/off call, so unsubscribing removes exactly
     * Drive's own handler and leaves handlers registered by add-ins in place (MYG-150343).
     */
    private readonly boundOnNativeNotificationEvent: TNativeActionEventCallback = this.onNativeNotificationEvent.bind(this);

    /**
     * Whether boundOnNativeNotificationEvent is registered for "click". The native bridges treat off() with a
     * handler they don't hold as "remove every handler for the event", so unsubscribing an instance that never
     * subscribed (a new Context's first startSubscriptions, or destroy after stopSubscriptions) would also remove
     * add-ins' handlers (MYG-150343).
     */
    private isNativeClickSubscribed = false;

    /**
     * Constructs a new instance of the Alerts service
     * @param store The DriveStore object
     * @param translationService The translation service
     */
    constructor(store: DriveStore, translationService: TranslationService) {
        super();
        this.store = store;
        this.translationService = translationService;
        this.pendingBackgroundAlerts = {};
    }

    /**
     * Prompts a user with an alert, that may include actions
     * @param message The alert prompt message
     * @param title The title of the alert prompt
     * @param actions A list of actions that will be presented to the user
     * @param translate Whether or not to translate the dialog
     */
    prompt(message: string, title?: string, actions?: IPromptAction[], translate = true): Promise<AlertPrompt> {
        return new Promise<AlertPrompt>(resolve => {
            let buttons: IDialogButton[] = [];
            actions && actions.forEach(action => {
                let button: IDialogButton = {
                    id: action.id,
                    text: action.name,
                    callback: (dialogRef, event) => {
                        dialogRef.remove();
                        action.action.bind(this)(dialogRef, event);
                    }
                };
                if (action.type === "secondary") {
                    button.className = "button_secondary";
                }
                buttons.push(button);
            });
            let dialogId = idSerializer.get();

            let dialog = new Dialog(
                dialogId,
                translate ? message : `<span class="noTranslate">${ htmlEscape(message)  }</span>`,
                this.translationService.getTranslator(),
                (translate || !title) ? title : `<span class="noTranslate">${ htmlEscape(title) }</span>`,
                buttons,
                { isNightMode: this.store.getState().data.nightMode }
            );
            let alert = new AlertPrompt(dialogId, dialog, actions, translate);

            /**
            * If we're in the foreground, just show a UI dialog.
            * If we're in the background, register and display a native OS notification (and event handlers for this)
            */
            if (this.store.getState().mobile.background) {
                registerActionListeners(alert, this.boundOnNativeNotificationEvent);
                logger.log(logger.Source.DEBUG, `nativeNotify: Notified alert prompt in background with message: "${ message }" (AppType: ${ AppType[getAppType()] })`);
                getNotificationController().nativeNotify(message, title, undefined, getNativeNotificationActions(alert)).then(notification => {
                    if (notification) {
                        alert.nativeNotification = notification;
                        this.addToPendingBackgroundAlerts(alert);
                    }
                    resolve(alert);
                }).catch((error) => {
                    logger.error(logger.Source.NATIVE, `nativeNotify: Failed to notify alert prompt in background: ${ error?.message || error }`);
                    resolve(alert);
                });
            } else {
                void alert.openDialog(this.translationService.getTranslator());
                resolve(alert);
            }
        });
    }

    /**
     * Event handler when a native OS notification is interacted with
     * @param nativeNotification The native OS notification reference
     * @param evt The event performed by the user
     */
    onNativeNotificationEvent(nativeNotification: INativeNotify, evt: INativeActionEvent) {
        if (nativeNotification && evt) {
            let nativeNotificationId = nativeNotification.id;
            let eventName = evt.event;
            let alerts = this.getPendingBackgroundAlerts(nativeNotificationId);
            if (alerts.length) {
                let alert = alerts[0];
                if (eventName === "click") {
                    // When the user clicks on the notification itself (but not on any actions)
                    alert && alert.openDialog(this.translationService.getTranslator());
                } else {
                    // When the user selects an action
                    let splitActionId = evt.event.split("|"); // This is the "full" action ID with the alert GUID
                    splitActionId.shift();
                    let actionId = splitActionId.join("|"); // Restore the original action string if it contains a "|"
                    alert.triggerAction(actionId);
                }
            }

            // Clean out any pending alerts from memory and event handlers for them
            this.clearPendingAlerts(nativeNotificationId);
        }
    }

    /**
     * Event handling for native OS notifications when the app comes back into the foreground
     */
    onBackgroundChange() {
        let mobileState = this.store.getState().mobile;
        if (mobileState.background === false) {
            getNotificationController().getActiveNotifications().then(activeNotifications => {
                // Gets any active native OS notifications
                activeNotifications.forEach(notification => {
                    // Tries to find if this notification is tracked by this class
                    let alerts = this.getPendingBackgroundAlerts(notification.id);
                    if (alerts.length) {
                        let alert = alerts[0];

                        // Opens a UI dialog (as this background notification was ignored)
                        // Then cancels the native OS notification
                        alert.openDialog(this.translationService.getTranslator())
                            .then(() => getNotificationController().cancelNativeNotification(notification.id))
                            .catch((err: Error) => logger.error(logger.Source.SERVICES, `Error in cancelNativeNotification() ${ err.message }`));
                    }
                });
                this.clearPendingAlerts();
            }).catch((err) => logger.error(logger.Source.SERVICES, `Error in getActiveNotifications() ${ err.message }`));
        }
    }

    /**
     * Starts the subscriptions for the SubscriptionServvice
     */
    startSubscriptions(): void {
        this.stopSubscriptions();
        getNotificationController().nativeNotifyEventOn("click", this.boundOnNativeNotificationEvent);
        this.isNativeClickSubscribed = true;
        this.storeUnsubscribe = observeStore(this.store, state => [state.mobile.background], this.onBackgroundChange.bind(this));
    }

    /**
     * Stops the subscriptions for the SubscriptionServvice
     */
    stopSubscriptions(): void {
        this.storeUnsubscribe && this.storeUnsubscribe();
        this.clearPendingAlerts();
        if (this.isNativeClickSubscribed) {
            getNotificationController().nativeNotifyEventOff("click", this.boundOnNativeNotificationEvent);
            this.isNativeClickSubscribed = false;
        }
    }

    destroy() {
        super.destroy();
        this.pendingBackgroundAlerts = {};
    }
    private storeUnsubscribe: Unsubscribe = () => { };

    /**
     * Adds an alert to the background alert storage
     * @param alert The alert to add to the pending background alerts
     */
    private addToPendingBackgroundAlerts(alert: AlertPrompt) {
        if (typeof (alert.nativeId) !== "undefined") {
            this.pendingBackgroundAlerts[alert.nativeId] = alert;
        }
    }

    /**
     * Gets the pending background alerts by native OS notification ID
     * @param nativeNotificationId If provided, only returns the specified alert
     */
    private getPendingBackgroundAlerts(nativeNotificationId?: number | string): AlertPrompt[] {
        if (typeof (nativeNotificationId) !== "undefined") {
            let alert = this.pendingBackgroundAlerts[nativeNotificationId.toString()];
            return alert ? [alert] : [];
        }
        return Object.keys(this.pendingBackgroundAlerts).reduce((backgroundAlerts, pendingBackgroundId) => {
            let alert = this.pendingBackgroundAlerts[pendingBackgroundId];
            if (!alert) {
                return backgroundAlerts;
            }
            backgroundAlerts.push(alert);
            return backgroundAlerts;
        }, [] as AlertPrompt[]);
    }

    /**
     * Clears the pending background alerts
     * @param nativeNotificationId If provided, only clears the specified alert
     */
    private clearPendingAlerts(nativeNotificationId?: number | string) {
        this.getPendingBackgroundAlerts(nativeNotificationId).forEach(alert => {
            unregisterActionListeners(alert, this.boundOnNativeNotificationEvent);
            if (typeof (alert.nativeId) !== "undefined") {
                delete this.pendingBackgroundAlerts[alert.nativeId];
            }
        });
    }
}
